import { translate, useMatchMobile } from '@avensia/excite';
import { createTransitionDecorator } from '@avensia/excite-ui';
import { type StyledDecorator, styled } from '@glitz/react';
import * as React from 'react';
import { Button } from 'webapp/components/button';
import { ArrowLeft, ArrowRight } from 'webapp/components/icons';
import { Theme, type ThemeType } from 'webapp/theme';

const opacityZeroDecorator = styled({
  opacity: 0,
});

const NavButton = styled(
  Button,
  createTransitionDecorator({ property: 'opacity color scale', duration: '0.2s', timingFunction: 'ease-in-out' }),
  {
    padding: { xy: 0 },
    position: 'absolute',
    top: 'calc(50% - 17px)',
    border: { xy: { style: 'none' } },
    width: 40,
    height: 40,
    borderRadius: '100vw',
    opacity: 1,
    translate: 'transformY(-50%)',
    ':active': {
      transform: 'scale(0.85)',
    },
  },
);

const arrowStyle = styled({
  height: 24,
  width: 24,
});

const LeftArrowIcon = styled(ArrowLeft, arrowStyle);

const RightArrowIcon = styled(ArrowRight, arrowStyle);

type ButtonProps = {
  onClick: () => void;
  type: 'prev' | 'next';
  enabled: boolean;
  position?: number | string;
  decorator?: StyledDecorator;
  as?: ThemeType;
};

export const ArrowButton = styled((props: ButtonProps) => {
  const { onClick, type, enabled, position, decorator, as = Theme.CarouselButton } = props;
  const isMobile = useMatchMobile();

  const arrowDecorator = React.useMemo(() => {
    if (type === 'prev') {
      if (isMobile) {
        return styled({
          left: position !== undefined ? position : t => t.spacing.xs,
        });
      }
      return styled({
        left: position !== undefined ? position : t => t.spacing.xxl,
      });
    } else if (type === 'next') {
      if (isMobile) {
        return styled({
          right: position !== undefined ? position : t => t.spacing.xs,
        });
      }
      return styled({
        right: position !== undefined ? position : t => t.spacing.xxl,
      });
    }
  }, [isMobile, position, type]);

  return (
    <NavButton
      onClick={onClick}
      as={as}
      css={styled(arrowDecorator, !enabled && opacityZeroDecorator, decorator && decorator)}
      disableRippleEffect
      aria-label={type === 'prev' ? translate(t => t.previous) : translate(t => t.next)}
    >
      {type === 'prev' ? <LeftArrowIcon aria-hidden /> : <RightArrowIcon aria-hidden />}
    </NavButton>
  );
});
